import { DragEvent, ChangeEvent } from 'react';
import { action, observable, makeAutoObservable } from 'mobx';
import api from '../../../api';
import { default as showError } from '../../../utils/error.jsx';
import { TranslationsContentState } from '../TranslationsContent.state';

type ConstructorProps = {
    translationsContentState: TranslationsContentState;
};

export class DropAreaState {
    @observable isUploading: boolean = false;
    @observable isHighlighted: boolean = false;
    @observable translationsContentState: TranslationsContentState;

    constructor({ translationsContentState }: ConstructorProps) {
        this.translationsContentState = translationsContentState;
        makeAutoObservable(this);
    }

    @action setUploading = (uploadState: boolean): void => {
        this.isUploading = uploadState;
    };

    @action setHighlighted = (highlightedState: boolean): void => {
        this.isHighlighted = highlightedState;
    };

    @action onDragEnterHandle = (event: DragEvent<HTMLDivElement>): void => {
        this.preventDefaults(event);
        this.setHighlighted(true);
    };

    @action onDragLeaveHandle = (event: DragEvent<HTMLDivElement>): void => {
        this.preventDefaults(event);
        this.setHighlighted(false);
    };

    @action onDropHandle = (event: DragEvent<HTMLDivElement>): void => {
        this.preventDefaults(event);
        this.setHighlighted(false);
        const files = event.dataTransfer.files;
        this.onUpload([...files]);
    };

    @action onSelectFilesHandle = (event: ChangeEvent<HTMLInputElement>): void => {
        const files = event.target.files;
        if (files) {
            this.onUpload([...files]);
        }
    };

    @action onUpload = (files: File[]): void => {
        if (this.isUploading) {
            return;
        }
        this.setUploading(true);

        const translationsFormElement = document.querySelector<HTMLFormElement>('.translations-content form');
        translationsFormElement?.reset();

        const promises: Promise<any>[] = [];
        const filesArray = Array.from(files);
        filesArray.forEach((file) => {
            const formData = new FormData();
            formData.set('translations', file);
            promises.push(
                api.postUpload(`/translations/${this.translationsContentState.groupName}/csv`, {
                    data: formData,
                })
            );
        });

        Promise.all(promises)
            .then(async () => {
                await this.translationsContentState.refreshLanguages();
            })
            .catch(() => {
                showError('Failed to upload all translations');
            })
            .finally(() => {
                this.setUploading(false);
            });
    };

    preventDefaults = (event: DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        event.stopPropagation();
    };
}
